/**
 * Vitrine inicial modular (estilo “marketplace”) — configurada em `tenant.theme_json.storeHomeLayout`.
 * O painel Empresas grava JSON + flag `enabled`; a loja pública só renderiza se `enabled === true`.
 */

/** Texto inicial sugerido no editor (sem a flag `enabled`, controlada à parte no formulário). */
export const STORE_HOME_LAYOUT_EDITOR_EXAMPLE = `{
  "hero": [
    {
      "enabled": true,
      "image_url": "/logo-ominia-hub-ring.svg",
      "title": "Destaque da loja",
      "subtitle": "Texto curto e link opcional",
      "href": ""
    }
  ],
  "sections": [
    {
      "type": "category_strip",
      "enabled": true,
      "title": "Compre por categoria",
      "item_shape": "circle",
      "tile_bg": "#e0f2fe",
      "items": [
        { "enabled": true, "label": "Ofertas", "image_url": "/favicon.svg", "secao": "" }
      ]
    },
    {
      "type": "produtos_destaque",
      "enabled": true,
      "title": "Em destaque na loja",
      "limit": 12
    },
    {
      "type": "product_row",
      "enabled": true,
      "title": "Mais vendidos",
      "product_ids": []
    },
    {
      "type": "card_grid",
      "enabled": true,
      "columns": 4,
      "cards": [
        {
          "enabled": true,
          "title": "Destaque",
          "variant": "quad",
          "product_ids": [],
          "footer_link_label": "Ver mais",
          "footer_href": ""
        }
      ]
    }
  ]
}`;

/** Secção / slide / cartão: `enabled === false` oculta na loja (mantém dados no JSON). */
export function isVitrineItemEnabled(obj) {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return true;
  if (Object.prototype.hasOwnProperty.call(obj, 'enabled') && obj.enabled === false) return false;
  return true;
}

/**
 * Aceita `[1, 2]`, `["3"]` ou `[{ "id": 1, "enabled": false }]`.
 * @param {unknown} raw
 * @returns {{ id: number; enabled: boolean }[]}
 */
export function normalizeVitrineProductIds(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const x of raw) {
    if (typeof x === 'number' && Number.isFinite(x) && x >= 1) {
      out.push({ id: Math.trunc(x), enabled: true });
      continue;
    }
    if (typeof x === 'string') {
      const n = Number(String(x).trim());
      if (Number.isFinite(n) && n >= 1) out.push({ id: Math.trunc(n), enabled: true });
      continue;
    }
    if (x && typeof x === 'object' && !Array.isArray(x)) {
      const id = Number(/** @type {{ id?: unknown }} */ (x).id);
      if (!Number.isFinite(id) || id < 1) continue;
      const enabled = /** @type {{ enabled?: unknown }} */ (x).enabled === false ? false : true;
      out.push({ id: Math.trunc(id), enabled });
    }
  }
  return out;
}

export function normalizeThemeJson(themeJson) {
  let obj = themeJson;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
  return { ...obj };
}

/**
 * @param {unknown} themeJson
 * @returns {{ enabled: boolean, hero: unknown[], sections: unknown[] }}
 */
export function parseStoreHomeLayout(themeJson) {
  const base = normalizeThemeJson(themeJson);
  const raw = base.storeHomeLayout;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { enabled: false, hero: [], sections: [] };
  }
  const enabled = Boolean(raw.enabled);
  let hero = [];
  if (Array.isArray(raw.hero)) hero = raw.hero;
  else if (Array.isArray(raw.heroSlides)) hero = raw.heroSlides;
  const sections = Array.isArray(raw.sections) ? raw.sections : [];
  return {
    enabled,
    hero: hero.filter((x) => x && typeof x === 'object'),
    sections: sections.filter((x) => x && typeof x === 'object' && typeof x.type === 'string'),
  };
}

/**
 * Corpo da vitrine para o editor (sem `enabled`).
 * @param {unknown} themeJson
 */
export function storeHomeLayoutBodyForEditor(themeJson) {
  const base = normalizeThemeJson(themeJson);
  const raw = base.storeHomeLayout;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { hero: [], sections: [] };
  }
  const { enabled: _e, ...rest } = raw;
  const out = { hero: [], sections: [], ...rest };
  if (!Array.isArray(out.hero)) out.hero = [];
  if (!Array.isArray(out.sections)) out.sections = [];
  delete out.enabled;
  return out;
}
